import { MouseEvent, RefObject, useEffect, useRef } from 'react';
import Hammer from 'hammerjs';

// QBank's long-press: the same hold time and movement tolerance.
const PRESS_TIME_MS = 500;
const PRESS_THRESHOLD_PX = 20;
// How long after a long-press ends the click it produces is still its click.
const PRESS_CLICK_WINDOW_MS = 800;
// A press whose release was never seen stops claiming clicks after this.
const UNRELEASED_PRESS_LIMIT_MS = 10_000;
// A long touch can raise both the press and a `contextmenu` (Android does);
// the second one within this window is the same gesture, not a second toggle.
const SAME_GESTURE_WINDOW_MS = 1000;
const OPTION_LETTER_ATTRIBUTE = 'data-option-letter';

type GestureToggle = {
  letter: string;
  source: 'press' | 'contextmenu';
  at: number;
  /** When the press was released; null while it is still held. */
  releasedAt: number | null;
};

const letterFromTarget = (
  container: HTMLElement,
  target: EventTarget | null
): string | null => {
  if (!(target instanceof Element)) return null;

  const option = target.closest(`[${OPTION_LETTER_ATTRIBUTE}]`);
  if (!option || !container.contains(option)) return null;

  return option.getAttribute(OPTION_LETTER_ATTRIBUTE);
};

/**
 * The QBank crossout gestures for the in-chat answer options (ORA-820):
 * right-click an option, or hold it for half a second. The options answer on
 * click, so the click that ends a long-press is swallowed — a crossout must
 * never submit the option it crosses out.
 *
 * Options mark themselves with `data-option-letter`; the long-press listener
 * sits on the container that holds them.
 */
export const useOptionCrossoutGestures = (
  containerRef: RefObject<HTMLElement>,
  {
    disabled,
    onToggle,
    scopeKey,
  }: {
    disabled: boolean;
    onToggle: (letter: string) => void;
    scopeKey: string;
  }
): {
  onOptionContextMenu: (letter: string, event: MouseEvent) => void;
  /** True (once) when this click is the end of a long-press on the option. */
  isLongPressClick: (letter: string) => boolean;
} => {
  const onToggleRef = useRef(onToggle);
  const lastToggle = useRef<GestureToggle | null>(null);

  useEffect(() => {
    onToggleRef.current = onToggle;
  }, [onToggle]);

  const isSameGesture = (
    letter: string,
    source: GestureToggle['source']
  ): boolean => {
    const last = lastToggle.current;

    return Boolean(
      last &&
      last.letter === letter &&
      last.source !== source &&
      Date.now() - last.at < SAME_GESTURE_WINDOW_MS
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) return;

    // Only the press recognizer: the default set would also claim horizontal
    // pans and taps, and the thread around the options has to keep scrolling.
    const hammer = new Hammer.Manager(container, {
      recognizers: [
        [Hammer.Press, { time: PRESS_TIME_MS, threshold: PRESS_THRESHOLD_PX }],
      ],
    });

    hammer.on('press', (event) => {
      const letter = letterFromTarget(container, event.target);
      if (!letter) return;

      event.srcEvent.preventDefault();

      if (!isSameGesture(letter, 'press')) onToggleRef.current(letter);

      lastToggle.current = {
        letter,
        source: 'press',
        at: Date.now(),
        releasedAt: null,
      };
    });

    hammer.on('pressup', () => {
      if (lastToggle.current?.source === 'press') {
        lastToggle.current.releasedAt = Date.now();
      }
    });

    return () => {
      hammer.destroy();
    };
  }, [containerRef, disabled, scopeKey]);

  const onOptionContextMenu = (letter: string, event: MouseEvent) => {
    // Once the question is answered the options are read-only; leave the
    // browser's own menu alone.
    if (disabled) return;

    event.preventDefault();

    // The press already toggled, and it still owns the click that ends the
    // touch: keep it as the last toggle so that click is not taken as an answer.
    if (isSameGesture(letter, 'contextmenu')) return;

    onToggleRef.current(letter);

    lastToggle.current = {
      letter,
      source: 'contextmenu',
      at: Date.now(),
      releasedAt: Date.now(),
    };
  };

  const isLongPressClick = (letter: string) => {
    const last = lastToggle.current;
    if (!last || last.source !== 'press' || last.letter !== letter) {
      return false;
    }

    const isFromThisPress =
      last.releasedAt === null
        ? Date.now() - last.at < UNRELEASED_PRESS_LIMIT_MS
        : Date.now() - last.releasedAt < PRESS_CLICK_WINDOW_MS;

    if (isFromThisPress) lastToggle.current = null;

    return isFromThisPress;
  };

  return { onOptionContextMenu, isLongPressClick };
};
